import { KnowledgeItem, KnowledgeItemType } from "./types";

type KnowledgePatchMode = "prepend" | "replace" | "move-to-top";
type KnowledgeMutationBaseArgs = { entityId: string; agentId: string };

type PatchKnowledgeListConfig<
  TType extends KnowledgeItemType,
  TArgs extends KnowledgeMutationBaseArgs,
> = {
  knowledgeType: TType | ((mutationArgs: TArgs) => TType);
  patchMode: KnowledgePatchMode;
  resolveSourceId?: (mutationArgs: TArgs) => string;
  updateKnowledgeCache: (params: {
    entityId: string;
    agentId: string;
    knowledgeType: TType;
    recipe: (draft: KnowledgeItem<TType>[]) => void;
  }) => unknown;
};

export const patchKnowledgeList =
  <TType extends KnowledgeItemType, TArgs extends KnowledgeMutationBaseArgs>({
    knowledgeType,
    patchMode,
    resolveSourceId,
    updateKnowledgeCache,
  }: PatchKnowledgeListConfig<TType, TArgs>) =>
  async (
    mutationArgs: TArgs,
    {
      dispatch,
      queryFulfilled,
    }: {
      dispatch: (action: unknown) => void;
      queryFulfilled: Promise<{ data: KnowledgeItem<TType>["data"] }>;
    }
  ) => {
    try {
      const { data: knowledgeItemData } = await queryFulfilled;
      const resolvedKnowledgeType =
        typeof knowledgeType === "function"
          ? knowledgeType(mutationArgs)
          : knowledgeType;

      dispatch(
        updateKnowledgeCache({
          entityId: mutationArgs.entityId,
          agentId: mutationArgs.agentId,
          knowledgeType: resolvedKnowledgeType,
          recipe: (knowledgeListDraft) => {
            if (patchMode === "prepend") {
              knowledgeListDraft.unshift({
                content_size: null,
                data: knowledgeItemData,
              } as KnowledgeItem<TType>);
              return;
            }

            const targetSourceId = resolveSourceId?.(mutationArgs);

            if (!targetSourceId) return;

            const itemIndex = knowledgeListDraft.findIndex(
              (item) => item.data.id === targetSourceId
            );

            if (itemIndex === -1) return;

            if (patchMode === "replace") {
              knowledgeListDraft[itemIndex] = {
                ...knowledgeListDraft[itemIndex],
                data: knowledgeItemData,
              };
              return;
            }

            const [itemToMove] = knowledgeListDraft.splice(itemIndex, 1);

            knowledgeListDraft.unshift({
              ...itemToMove,
              data: knowledgeItemData,
            });
          },
        })
      );
    } catch (_error) {
      /* noop */
    }
  };
